import { defineStore } from 'pinia';

/**
 * Product Cache Store
 *
 * This store manages caching of product listing data based on category ID and filters.
 *
 * Key Features:
 * - Caches products by category ID and active filters
 * - Automatically clears cache when category changes
 * - Supports pagination by storing multiple pages per cache entry
 * - Prevents unnecessary API calls when data is already cached
 *
 * Usage Flow:
 * 1. User navigates to category -> cache is checked for existing data
 * 2. If cache exists, products are loaded instantly from cache
 * 3. If no cache, API call is made and result is stored in cache
 * 4. User loads more products (pagination) -> new pages are added to existing cache
 * 5. User changes category -> cache is cleared and new cache is created
 * 6. User applies filters -> cache is cleared (filters change the data set)
 */

interface ProductPage {
    items: any[];
    ids: any[];
    page: number;
    actualProducts?: any[]; // Optional actual Shopware product data
}

interface ProductCacheData {
    categoryId: string;
    products: ProductPage[];
    filters: any[];
    properties: any;
    sorts: any[];
    activeFilters: string;
    requestId: string;
    totalPages: number;
    lastUpdated: number;
}

interface CacheEntry {
    [key: string]: ProductCacheData;
}

export const useProductCacheStore = defineStore('productCache', {
    state: () => ({
        cache: {} as CacheEntry,
        /**
         * Tracks the currently active listing "group" key.
         *
         * Note: despite the name, this is used as a listing identifier (category/manufacturer/search context),
         * not strictly a navigation category id.
         */
        currentCategoryId: '' as string,
    }),

    actions: {
        /**
         * Keep cache bounded to avoid unbounded memory growth in long sessions.
         * Removes the oldest entries by `lastUpdated` when we exceed `maxEntries`.
         */
        pruneCache(maxEntries: number = 30): void {
            const keys = Object.keys(this.cache);
            if (keys.length <= maxEntries) return;

            const entriesSortedByAge = keys
                .map(key => ({ key, lastUpdated: this.cache[key]?.lastUpdated ?? 0 }))
                .sort((a, b) => a.lastUpdated - b.lastUpdated);

            const toRemoveCount = Math.max(0, entriesSortedByAge.length - maxEntries);
            for (let i = 0; i < toRemoveCount; i++) {
                delete this.cache[entriesSortedByAge[i].key];
            }
        },

        /**
         * Explicitly set the current listing key.
         * Useful when rendering from cache without calling `setCache()` (no writes).
         */
        setCurrentCategoryId(categoryId: string): void {
            this.currentCategoryId = categoryId;
        },

        /**
         * Generate cache key from category ID and filters
         */
        generateCacheKey(categoryId: string, activeFilters: string = ''): string {
            // Create a consistent key that includes category and any active filters
            const sanitizedFilters = activeFilters.replace(/&tn_p=\d+/g, ''); // Remove pagination from cache key
            return `${categoryId}${sanitizedFilters ? `_${btoa(sanitizedFilters)}` : ''}`;
        },

        /**
         * Check if we have cached data for this category and filters
         */
        hasCache(categoryId: string, activeFilters: string = ''): boolean {
            const cacheKey = this.generateCacheKey(categoryId, activeFilters);
            return !!this.cache[cacheKey];
        },

        /**
         * Get cached data for category and filters
         */
        getCache(categoryId: string, activeFilters: string = ''): ProductCacheData | null {
            const cacheKey = this.generateCacheKey(categoryId, activeFilters);
            return this.cache[cacheKey] || null;
        },

        /**
         * Set cache data for category and filters
         */
        setCache(
            categoryId: string,
            activeFilters: string = '',
            data: Omit<ProductCacheData, 'categoryId' | 'lastUpdated'>,
        ): void {
            const cacheKey = this.generateCacheKey(categoryId, activeFilters);

            this.cache[cacheKey] = {
                ...data,
                categoryId,
                lastUpdated: Date.now(),
            };

            this.currentCategoryId = categoryId;
            this.pruneCache();
        },

        /**
         * Add a new page of products to existing cache
         */
        addPageToCache(categoryId: string, activeFilters: string = '', newPage: ProductPage): boolean {
            const cacheKey = this.generateCacheKey(categoryId, activeFilters);
            const existingCache = this.cache[cacheKey];

            if (!existingCache) {
                return false;
            }

            // Check if this page already exists
            const pageExists = existingCache.products.some(p => p.page === newPage.page);
            if (pageExists) {
                return true;
            }

            // Add the new page to products
            existingCache.products.push(newPage);
            existingCache.lastUpdated = Date.now();

            this.pruneCache();
            return true;
        },

        /**
         * Clear cache when category changes or reset is needed
         */
        clearCache(): void {
            this.cache = {};
            this.currentCategoryId = '';
        },

        /**
         * Clear specific cache entry
         */
        clearCacheEntry(categoryId: string, activeFilters: string = ''): void {
            const cacheKey = this.generateCacheKey(categoryId, activeFilters);
            delete this.cache[cacheKey];
        },

        /**
         * Check if category changed and clear if needed
         */
        checkAndClearOnCategoryChange(newCategoryId: string): boolean {
            // We intentionally do NOT clear the whole cache anymore.
            // This enables caching across multiple listings (e.g. search -> category -> back to search).
            const changed = !!this.currentCategoryId && this.currentCategoryId !== newCategoryId;
            this.currentCategoryId = newCategoryId;
            return changed;
        },

        /**
         * Get products for a specific page range
         */
        getProductsUpToPage(categoryId: string, activeFilters: string = '', maxPage: number): ProductPage[] {
            const cached = this.getCache(categoryId, activeFilters);
            if (!cached) return [];

            return cached.products.filter(p => p.page <= maxPage).sort((a, b) => a.page - b.page);
        },

        /**
         * Get actual products for a specific page only
         *
         * @param categoryId - The category ID
         * @param activeFilters - The active filters string (without pagination)
         * @param pageNumber - Specific page number to get products for
         * @returns Array of actual Shopware product objects for the specified page only
         *
         * @example
         * // Get actual products only for page 2
         * const page2Products = store.getActualProductsForPage('categoryId', '&filter=value', 2);
         */
        getActualProductsForPage(categoryId: string, activeFilters: string = '', pageNumber: number): any[] {
            const cached = this.getCache(categoryId, activeFilters);
            if (!cached) return [];

            const pageData = cached.products.find(p => p.page === pageNumber);
            return pageData?.actualProducts || [];
        },

        /**
         * Get all actual products for the current category (no parameters needed)
         * Returns all actual products from all cache entries for the current category
         *
         * @returns Array of all actual Shopware product objects for the current category
         *
         * @example
         * // Simply get all current actual products
         * const products = store.getCurrentActualProducts();
         */
        getCurrentActualProducts(): any[] {
            if (!this.currentCategoryId) {
                return [];
            }

            // Get all cache entries for the current category
            const categoryEntries = Object.entries(this.cache).filter(
                ([key, data]) => data.categoryId === this.currentCategoryId,
            );

            // Collect all actual products from all cache entries (different filter combinations)
            const allActualProducts: any[] = [];

            categoryEntries.forEach(([cacheKey, data]) => {
                data.products.forEach(pageData => {
                    if (pageData.actualProducts && Array.isArray(pageData.actualProducts)) {
                        allActualProducts.push(...pageData.actualProducts);
                    }
                });
            });

            return allActualProducts;
        },

        /**
         * Check if we have all pages up to a certain page number
         */
        hasAllPagesUpTo(categoryId: string, activeFilters: string = '', pageNumber: number): boolean {
            const cached = this.getCache(categoryId, activeFilters);
            if (!cached) return false;

            for (let i = 1; i <= pageNumber; i++) {
                const hasPage = cached.products.some(p => p.page === i);
                if (!hasPage) return false;
            }
            return true;
        },

        /**
         * Get cache statistics for debugging
         */
        getCacheStats() {
            return {
                totalEntries: Object.keys(this.cache).length,
                currentCategoryId: this.currentCategoryId,
                entries: Object.entries(this.cache).map(([key, data]) => ({
                    key,
                    categoryId: data.categoryId,
                    pagesCount: data.products.length,
                    totalProducts: data.products.reduce((sum, page) => sum + page.items.length, 0),
                    lastUpdated: new Date(data.lastUpdated).toISOString(),
                })),
            };
        },

        /**
         * Get current category statistics
         */
        getCurrentCategoryStats() {
            if (!this.currentCategoryId) {
                return {
                    isActive: false,
                    categoryId: null,
                    message: 'No active category',
                };
            }

            // Find all cache entries for the current category
            const categoryEntries = Object.entries(this.cache).filter(
                ([key, data]) => data.categoryId === this.currentCategoryId,
            );

            if (categoryEntries.length === 0) {
                return {
                    isActive: true,
                    categoryId: this.currentCategoryId,
                    hasCache: false,
                    message: 'No cached data for current category',
                };
            }

            // Get stats for each cache entry (different filter combinations)
            const filterStats = categoryEntries.map(([cacheKey, data]) => {
                const totalLoadedProducts = data.products.reduce((sum, page) => sum + page.items.length, 0);
                const totalActualProducts = data.products.reduce(
                    (sum, page) => sum + (page.actualProducts?.length || 0),
                    0,
                );
                const loadedPages = data.products.map(p => p.page).sort((a, b) => a - b);
                const hasAllPages = data.totalPages > 0 ? loadedPages.length === data.totalPages : false;

                return {
                    cacheKey,
                    activeFilters: data.activeFilters || 'No filters',
                    pagesLoaded: data.products.length,
                    loadedPageNumbers: loadedPages,
                    totalLoadedProducts,
                    totalActualProducts, // New: Count of actual Shopware products
                    totalAvailableProducts: data.properties?.nrofitems || 0,
                    totalPages: data.totalPages,
                    hasAllPages,
                    loadingProgress: data.totalPages > 0 ? Math.round((loadedPages.length / data.totalPages) * 100) : 0,
                    actualProductsProgress:
                        totalLoadedProducts > 0 ? Math.round((totalActualProducts / totalLoadedProducts) * 100) : 0,
                    lastUpdated: new Date(data.lastUpdated).toISOString(),
                    requestId: data.requestId,
                    filtersCount: data.filters?.length || 0,
                    sortsAvailable: data.sorts?.length || 0,
                };
            });

            // Calculate overall category stats
            const totalCachedProducts = filterStats.reduce((sum, stat) => sum + stat.totalLoadedProducts, 0);
            const totalActualProducts = filterStats.reduce((sum, stat) => sum + stat.totalActualProducts, 0);
            const totalUniquePages = new Set(filterStats.flatMap(stat => stat.loadedPageNumbers)).size;

            return {
                isActive: true,
                categoryId: this.currentCategoryId,
                hasCache: true,
                cacheEntriesCount: categoryEntries.length,
                totalCachedProducts,
                totalActualProducts, // New: Total actual Shopware products
                totalUniquePages,
                filterCombinations: filterStats,
                summary: {
                    mostRecentUpdate: Math.max(...categoryEntries.map(([, data]) => data.lastUpdated)),
                    oldestUpdate: Math.min(...categoryEntries.map(([, data]) => data.lastUpdated)),
                    averageProductsPerEntry: Math.round(totalCachedProducts / categoryEntries.length),
                    averageActualProductsPerEntry: Math.round(totalActualProducts / categoryEntries.length),
                    actualProductsLoadedPercentage:
                        totalCachedProducts > 0 ? Math.round((totalActualProducts / totalCachedProducts) * 100) : 0,
                },
            };
        },
    },

    getters: {
        /**
         * Get current cache entry
         */
        getCurrentCache: state => {
            if (!state.currentCategoryId) return null;
            return Object.values(state.cache).find(entry => entry.categoryId === state.currentCategoryId) || null;
        },

        /**
         * Check if current category has any cache
         */
        hasCurrentCache: state => {
            return (
                !!state.currentCategoryId &&
                Object.values(state.cache).some(entry => entry.categoryId === state.currentCategoryId)
            );
        },
    },
});
